import { getTranslations } from 'next-intl/server';
import React from 'react';

import {
  catalogService,
  productService,
  MappedProductList
} from '@/fo/api/magento';
import { cartActions } from '@/fo/store/product';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { MappedProduct, utilMapProductData } from '@/fo/util/map-product-data';

export type LandingProductInfo = {
  basePrice: number;
  finalPrice: number | null;
  name?: string;
  volume?: string;
  brand?: string;
  series?: string;
  baseImage?: { src: string; alt: string } | null;
  configurableVariants?: MappedProduct['configurableVariants'];
};

export type LandingProductInfoBySku = Record<string, LandingProductInfo>;

export type LandingProduct = {
  sku?: string;
  heading?: string | React.ReactNode;
  name?: string;
  link?: string;
  basePrice?: number;
  finalPrice?: number | null;
  volume?: string;
  brand?: string;
  series?: string;
  baseImage?: { src: string; alt: string } | null;
  configurableVariants?: MappedProduct['configurableVariants'];
};

export async function fetchLandingProductInfo(
  skus: string[]
): Promise<LandingProductInfoBySku> {
  const uniqueSkus = Array.from(new Set((skus || []).filter(Boolean)));
  if (uniqueSkus.length === 0) return {};

  const products = await productService.getProductsBySkus(uniqueSkus);
  const existingProducts = products.filter(
    (p): p is NonNullable<typeof p> => p !== null
  );
  if (existingProducts.length === 0) return {};

  const chunkSize = 3;
  const chunks = [];
  for (let i = 0; i < existingProducts.length; i += chunkSize) {
    chunks.push(existingProducts.slice(i, i + chunkSize));
  }

  const allPrices: MappedProductList = {};
  for (const chunk of chunks) {
    const pricesByKey = await catalogService.getPrices(chunk, false);
    Object.assign(allPrices, pricesByKey);
  }

  const t = await getTranslations();

  const result: LandingProductInfoBySku = {};
  existingProducts.forEach((product) => {
    const sku = product.sku || '';
    if (!sku) return;

    const mappedProduct = utilMapProductData(product, t, allPrices, {
      preselectFirstInStock: true
    });

    result[sku] = {
      basePrice: mappedProduct.basePrice ?? 0,
      finalPrice: mappedProduct.finalPrice ?? null,
      name: mappedProduct.name,
      volume: mappedProduct.volume,
      brand: mappedProduct.brand,
      series: mappedProduct.series,
      baseImage: mappedProduct.baseImage,
      configurableVariants: mappedProduct.configurableVariants
    };
  });

  return result;
}

export function mergeLandingProductInfo<T extends { sku?: string }>(
  items: T[],
  prices: LandingProductInfoBySku
): (T & {
  basePrice?: number;
  finalPrice?: number | null;
  name?: string;
  volume?: string;
  brand?: string;
  series?: string;
  baseImage?: { src: string; alt: string } | null;
  configurableVariants?: MappedProduct['configurableVariants'];
})[] {
  return (items || []).map((item) => {
    const sku = item?.sku || '';
    const entry = sku ? prices[sku] : undefined;
    return entry
      ? {
          ...item,
          basePrice: entry.basePrice,
          finalPrice: entry.finalPrice,
          name: entry.name,
          volume: entry.volume,
          brand: entry.brand,
          series: entry.series,
          baseImage: entry.baseImage ?? null,
          configurableVariants: entry.configurableVariants
        }
      : { ...item };
  });
}

function mapLandingProductToMapped(product: LandingProduct): MappedProduct {
  return {
    sku: product.sku ?? '',
    parentSku: product.sku,
    name:
      product.name ||
      (typeof product.heading === 'string' ? product.heading : ''),
    brand: product.brand || '',
    series: product.series || '',
    volume: product.volume,
    mindboxId: product.sku ?? '',
    basePrice: product.basePrice ?? null,
    finalPrice: product.finalPrice ?? null,
    baseImage: product.baseImage ?? null,
    configurableVariants: product.configurableVariants,
    stockStatus: 'IN_STOCK',
    stockQuantity: 999,
    labelsBrand: [],
    link: product.link || '',
    thumbnail: product.baseImage ?? null,
    brandCode: '',
    catalogBrandUrl: '',
    subtitle: '',
    shortDescription: '',
    labelsBonus: [],
    labels: [],
    structureImage: null,
    mediaGallery: [],
    rating: 0,
    reviewCount: 0,
    bestRating: 5,
    worstRating: 1,
    bonusPoints: 0,
    bonusPointsMax: 0,
    earnedBonusPoints: 0,
    effects: '',
    benefits: '',
    description_html: '',
    characteristics: [],
    activeComponents: [],
    composition: '',
    usageInstructions: '',
    firstLevelCategory: null,
    lastLevelCategory: null
  };
}

export function addLandingProductToCart(product: LandingProduct) {
  const isConfigurable =
    product.configurableVariants && product.configurableVariants.length > 1;

  if (isConfigurable) {
    utilEventConfigurableProduct.open(mapLandingProductToMapped(product));
  } else {
    cartActions.addProductsToCart(
      [
        {
          sku: product.sku ?? '',
          quantity: 1,
          product: {
            sku: product.sku ?? '',
            name:
              product.name ||
              (typeof product.heading === 'string' ? product.heading : ''),
            brand: product.brand || '',
            series: product.series || '',
            volume: product.volume || '',
            basePrice: product.basePrice || 0,
            finalPrice: product.finalPrice || null,
            baseImage: product.baseImage || { src: '', alt: '' }
          }
        }
      ],
      { openInNewTab: true }
    );
  }
}
